import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import logger from "../utils/logger";
import {
  Activity, Upload, Shield, AlertTriangle, Clock, ChevronDown,
  ToggleLeft, ToggleRight, FileSpreadsheet, Filter, User,
} from "lucide-react";
import { CollectorFreshnessBanner } from "../components/CollectorFreshnessBanner";

const API = `${window.location.origin}/api`;

const STATUS_STYLES = {
  open: { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", label: "Open" },
  under_review: { color: "#2563eb", bg: "rgba(37,99,235,0.12)", label: "Under Review" },
  cleared: { color: "#10b981", bg: "rgba(16,185,129,0.12)", label: "Cleared" },
  escalated: { color: "#ef4444", bg: "rgba(239,68,68,0.12)", label: "Escalated" },
};

const RISK_STYLES = {
  low: { color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  medium: { color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  high: { color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
  critical: { color: "#dc2626", bg: "rgba(220,38,38,0.15)" },
};

const ANALYSTS = ["Priya Sharma", "Rahul Verma", "Anita Desai"];

function RulesPanel() {
  const [rules, setRules] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/transactions/rules`, { withCredentials: true });
        setRules(data.rules || []);
      } catch (err) { logger.error("Failed to load rules:", err); }
    })();
  }, []);

  const toggleRule = async (ruleId, enabled) => {
    try {
      await axios.put(`${API}/transactions/rules/${ruleId}`, { enabled: !enabled }, { withCredentials: true });
      setRules(prev => prev.map(r => r.id === ruleId ? { ...r, enabled: !r.enabled } : r));
    } catch (err) { logger.error("Failed to toggle rule:", err); }
  };

  return (
    <div className="card-aml" data-testid="rules-panel">
      <div className="flex items-center gap-2 mb-4">
        <Shield style={{ width: "16px", height: "16px", color: "#2563eb" }} />
        <h2 style={{ fontSize: "14px", fontWeight: 700, color: "#f1f5f9" }}>Rule Engine</h2>
        <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "10px", background: "rgba(37,99,235,0.12)", color: "#60a5fa" }}>
          {rules.filter(r => r.enabled).length} Active
        </span>
      </div>
      <div className="space-y-2">
        {rules.map(rule => (
          <div key={rule.id} data-testid={`rule-${rule.id}`} style={{
            background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px",
            padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between",
            opacity: rule.enabled ? 1 : 0.5, transition: "opacity 0.2s",
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#f1f5f9" }}>{rule.name}</div>
              <div style={{ fontSize: "10px", color: "#475569" }}>{rule.description}</div>
            </div>
            <div className="flex items-center gap-3">
              <span style={{ fontSize: "11px", fontWeight: 700, color: "#f59e0b" }}>{rule.alerts_triggered} alerts</span>
              <button onClick={() => toggleRule(rule.id, rule.enabled)} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
                {rule.enabled
                  ? <ToggleRight style={{ width: "24px", height: "24px", color: "#10b981" }} />
                  : <ToggleLeft style={{ width: "24px", height: "24px", color: "#475569" }} />
                }
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TransactionUpload({ onUploadComplete }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/transactions/upload`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setFile(null);
      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      logger.error("Upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card-aml" data-testid="txn-upload-panel">
      <div className="flex items-center gap-2 mb-4">
        <Upload style={{ width: "16px", height: "16px", color: "#FF6B35" }} />
        <h2 style={{ fontSize: "14px", fontWeight: 700, color: "#f1f5f9" }}>Upload Transactions</h2>
      </div>
      <div style={{ border: "2px dashed #1e2530", borderRadius: "8px", padding: "20px", textAlign: "center", background: "#080c12" }}>
        {!file ? (
          <label style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)", borderRadius: "8px",
            padding: "8px 18px", cursor: "pointer", color: "#fff", fontSize: "12px", fontWeight: 600,
          }}>
            <FileSpreadsheet style={{ width: "14px", height: "14px" }} /> Choose CSV
            <input type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} style={{ display: "none" }} data-testid="txn-file-input" />
          </label>
        ) : (
          <div className="flex items-center justify-center gap-3">
            <span style={{ fontSize: "13px", color: "#f1f5f9" }}>{file.name}</span>
            <button onClick={handleUpload} disabled={uploading} data-testid="txn-upload-btn" style={{
              background: "linear-gradient(135deg, #10b981, #059669)", border: "none", borderRadius: "8px",
              padding: "8px 18px", cursor: "pointer", color: "#fff", fontSize: "12px", fontWeight: 600,
            }}>
              {uploading ? "Processing..." : "Upload & Analyze"}
            </button>
          </div>
        )}
        <p style={{ fontSize: "10px", color: "#475569", marginTop: "8px" }}>Columns: Date, Amount, Type, Counterparty Name, Counterparty Account, Description</p>
      </div>
      {result && (
        <div data-testid="txn-upload-result" style={{
          marginTop: "12px", padding: "12px", borderRadius: "8px",
          background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.15)",
        }}>
          <span style={{ fontSize: "12px", color: "#10b981", fontWeight: 600 }}>
            Processed {result.total_transactions} transactions — {result.alerts_generated} alerts generated
          </span>
        </div>
      )}
    </div>
  );
}

function AlertsTable() {
  const [alerts, setAlerts] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      const { data } = await axios.get(`${API}/transactions/alerts?${params}`, { withCredentials: true });
      setAlerts(data.alerts || []);
    } catch (err) { logger.error("Failed to load alerts:", err); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const updateAlert = async (alertId, field, value) => {
    try {
      await axios.put(`${API}/transactions/alerts/${alertId}`, { [field]: value }, { withCredentials: true });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, [field]: value } : a));
    } catch (err) { logger.error("Failed to update alert:", err); }
  };

  return (
    <div className="card-aml" data-testid="alerts-table" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #1e2530" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle style={{ width: "16px", height: "16px", color: "#ef4444" }} />
            <h2 style={{ fontSize: "14px", fontWeight: 700, color: "#f1f5f9" }}>Transaction Alerts</h2>
            <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "10px", background: "rgba(239,68,68,0.12)", color: "#ef4444" }}>
              {alerts.filter(a => a.status === "open").length} Open
            </span>
          </div>
          <div className="flex gap-1">
            {["", "open", "under_review", "escalated", "cleared"].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)} style={{
                padding: "4px 10px", borderRadius: "6px", fontSize: "10px", fontWeight: 600, cursor: "pointer",
                border: filterStatus === s ? "1px solid #2563eb" : "1px solid #1e2530",
                background: filterStatus === s ? "rgba(37,99,235,0.12)" : "transparent",
                color: filterStatus === s ? "#60a5fa" : "#94a3b8",
              }}>
                {s ? (STATUS_STYLES[s]?.label || s) : "All"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#475569" }}>Loading alerts...</div>
      ) : alerts.length === 0 ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#475569" }}>No alerts found</div>
      ) : (
        <>
          <div style={{
            display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 0.8fr 0.8fr 1fr 1fr",
            gap: "8px", padding: "10px 20px", background: "#080c12",
            fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.2px", color: "#475569",
          }}>
            <span>Rule / Alert</span><span>Customer</span><span>Amount</span><span>Date</span><span>Risk</span><span>Status</span><span>Assign</span>
          </div>
          {alerts.slice(0, 15).map(a => {
            const rs = RISK_STYLES[a.risk_level] || RISK_STYLES.medium;
            const ss = STATUS_STYLES[a.status] || STATUS_STYLES.open;
            return (
              <div key={a.id} data-testid={`alert-${a.id}`} style={{
                display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 0.8fr 0.8fr 1fr 1fr",
                gap: "8px", padding: "12px 20px", borderTop: "1px solid #0f1520", alignItems: "center",
              }}>
                <span style={{ fontSize: "12px", color: "#f1f5f9", fontWeight: 500 }}>{a.rule_name}</span>
                <span style={{ fontSize: "12px", color: "#94a3b8" }}>{a.customer_name}</span>
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#f1f5f9" }}>
                  {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(a.amount)}
                </span>
                <span style={{ fontSize: "11px", color: "#475569" }}>{a.date}</span>
                <span style={{ fontSize: "9px", fontWeight: 700, padding: "2px 6px", borderRadius: "3px", background: rs.bg, color: rs.color, textTransform: "uppercase" }}>
                  {a.risk_level}
                </span>
                <select
                  value={a.status}
                  onChange={e => updateAlert(a.id, "status", e.target.value)}
                  style={{
                    background: ss.bg, border: `1px solid ${ss.color}33`, borderRadius: "4px",
                    padding: "3px 6px", color: ss.color, fontSize: "10px", fontWeight: 600, cursor: "pointer",
                  }}
                >
                  <option value="open">Open</option>
                  <option value="under_review">Under Review</option>
                  <option value="escalated">Escalated</option>
                  <option value="cleared">Cleared</option>
                </select>
                <select
                  value={a.assigned_to || ""}
                  onChange={e => updateAlert(a.id, "assigned_to", e.target.value || null)}
                  style={{
                    background: "#080c12", border: "1px solid #1e2530", borderRadius: "4px",
                    padding: "3px 6px", color: "#94a3b8", fontSize: "10px", cursor: "pointer",
                  }}
                >
                  <option value="">Unassigned</option>
                  {ANALYSTS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default function TransactionMonitoringPage() {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div data-testid="txn-monitoring-page">
      <div className="mb-6">
        <h1 style={{ fontSize: "26px", fontWeight: 700, letterSpacing: "-0.5px", color: "#f1f5f9", marginBottom: "4px" }} data-testid="txn-monitoring-title">
          Transaction Monitoring
        </h1>
        <p style={{ color: "#94a3b8", fontSize: "14px" }}>Rule-based transaction surveillance and alert management</p>
      </div>

      <CollectorFreshnessBanner collectorKey="transactions" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <RulesPanel />
        <TransactionUpload onUploadComplete={() => setRefreshKey(k => k + 1)} />
      </div>

      <AlertsTable key={refreshKey} />
    </div>
  );
}
